import React, { useState, useEffect } from 'react';
import { Container, Form, Alert, Button } from 'react-bootstrap';
import { Auth } from 'aws-amplify';
import './Register.css';
import LoaderButton from '../components/LoaderButton';
import PasswordField from '../components/PasswordField';
import { useAppContext } from '../libs/contextLib';
import { useFormFields } from '../libs/hooksLib';
import { UiError } from '../libs/errorLib';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import config from '../config';

export default function Register() {
  const { setError }                          = useAppContext();
  // We initialise to true so we don't start validatring the form until the user
  // has clicked submit the first time. This prevent sannoying messages form
  // showing until the user is 'done'.
  const [isValid, setIsValid]             = useState(true);
  const { setUser }                       = useAppContext();
  const [isLoading, setIsLoading]         = useState(false);
  const { trackEvent }                    = useMatomo();
  const [ captchaValue, setCaptchaValue ] = useState(null);
  const navigate                           = useNavigate();
  const [fields, handleFieldChange]       = useFormFields({
    email: '',
    password: '',
    password_repeat: ''
  });
  // Password complexity check from:
  // https://stackoverflow.com/questions/58767980/aws-cognito-password-regex-specific-to-aws-cognito
  // eslint-disable-next-line 
  const passwordRegexp = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[=+\-^$*.\[\]{}()?"!@#%&/\\,><':;|_~`])\S{8,99}$/)
  const passwordError = (
  <div>Please make sure your password is complex enough:
    <ul>
      <li key='1'>Is between 8 and 99 characters long.</li>
      <li key='2'>Contains at least one number.</li>
      <li key='3'>Contains at least one upper- and lower-case letter.</li>
      <li key='4'>Contains at least one of the following special characters:<br/>
      ^ $ * . [ ] { } ( ) ? &quot; ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ `</li>
    </ul>
  </div>)


  /**
   * Called after fields is updated in handleFieldChange.
   * Will validate form if state currently isn't valid.
   */
  useEffect(() => {
      if (!isValid) {
        validateForm();
      }
    },
    // eslint-disable-next-line
    [isValid, fields]
  );

  /**
   * Validates the form, sets error messages and isValid accordingly.
   */
  function validateForm() {
    let valid = true;

    if (
      fields.email.length === 0 ||
      fields.password.length === 0 ||
      fields.password_repeat.length === 0
    ) {
      valid = false;
      setError(
        new UiError('register',  'Please ensure all form field are filled.')
      );
    }

    if (
      fields.password !== fields.password_repeat
    ) {
      valid = false;
      setError(
        new UiError('register',  'Please ensure your passwords match.')
      );
    }

    if (!passwordRegexp.test(fields.password)) {
      valid = false;
      setError(
        new UiError(
          'register', passwordError)
      );
    }

    if(captchaValue === null) {
      valid = false;
      setError(
        new UiError('register',  'Please solve the captcha.')
      );
    }

    // Potentially clear errors.
    if (valid) {
      setError(
        new UiError('register',  '', 'resolved')
      );
    }
    setIsValid(valid);
    return valid;
  }

  /**
   * Function to handle the user hitting submit.
   * Registers the user and sets the user in the context.
   * @param {event} event 
   */
  async function handleSubmit(event) {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    // Track the event.
    trackEvent({
      category: 'identity',
      action: 'register',
      name: 'set',
    });
    const newUserData = {
      username: fields.email,
      password: fields.password,
      attributes: {
        'custom:type': '00100000'
      },
      validationData: {
        captchaValue: captchaValue
      },
    }
    // We'll set the email so we can potentially use it in Confirm.
    setUser({username: fields.email});
    try {
      // AuthManager will pick up the effect of this.
      await Auth.signUp(newUserData);
    } catch (e) {
      console.error(e);
      //setError({key: 'register', message: "Couldn't register: " + e.message, severity: 'danger'});
    }
    setIsLoading(false);
  }

  function getRegisterCard() {
    if (config.featureFlags.showFirmRegistration) {
      return (
        <>
          <h1 className="h3 mb-3 font-weight-normal">Firm Registration</h1>
          <Alert variant="success">
            If your firm does not have terms of business with Cirencester
            Friendly, please register your firm&nbsp;
            <a
              href={config.links.firmRegistration}
            >
              here
            </a>
            &nbsp;before continuing.
          </Alert>
        </>
      )
    }
  }

  /**
   * Returns captcha if it's enabled.
   */
  function getCaptcha() {
    if (!config.captcha.enabled) {
      return(null);
    }
    return (
      <div className="recaptchaContainer">
        <ReCAPTCHA
          sitekey = { config.captcha.siteKey }
          onChange = { 
            (value) => {
              setCaptchaValue(value)
            }
          }
          onExpired = { () => setCaptchaValue(null) }
        />
      </div>
    )
  }

  return (
    <Container fluid="sm">
      <div className="text-center">
        <Form className="form-register" onSubmit={handleSubmit}>
          { getRegisterCard() }

          <h1 className="h3 mb-3 font-weight-normal">Create your account</h1> 

          <div className="d-grid gap-2">
            <div>
              <Form.Control 
                autoFocus
                id="email"
                placeholder="Email address"
                type="email"
                autoComplete="username"
                value={fields.email}
                onChange={handleFieldChange}
              />
              <Form.Label htmlFor="email" className="form-label visually-hidden">Email</Form.Label>
            </div>

            <div>
              <PasswordField
                id="password"
                placeholder="Password"
                label="Password"
                autoComplete="new-password"
                value={fields.password}
                onChange={handleFieldChange}
                className="topinput"
              />
              <PasswordField
                id="password_repeat"
                placeholder="Repeat password"
                label="Repeat password"
                autoComplete="new-password"
                value={fields.password_repeat}
                onChange={handleFieldChange}
                className="bottominput"
              />
            </div>

            { getCaptcha() }
            <LoaderButton
              disabled={!isValid}
              size="large"
              type="submit"
              isLoading={isLoading}
            >
              Register
            </LoaderButton>
            <Button variant="secondary" onClick={() => navigate.goBack()}>Back</Button>
          </div>
        </Form>
      </div>
    </Container>
  );
}
